import React, { useRef } from "react";
import { HourlyDispatchResult } from "../../powerFlow/multiTimestepOptimization";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import { renderGrid, renderAxes } from "../../bezier/curveRenderer";

interface Props {
  batteries: any[];
  hourlyData: HourlyDispatchResult[];
  numTimeSteps: number;
  hoverHour?: number | null;
  onHourHover?: (hour: number | null) => void;
}

const BatterySOCChart: React.FC<Props> = ({
  batteries,
  hourlyData,
  numTimeSteps,
  hoverHour,
  onHourHover,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const legendWidth = 120;
  const dimensions = useElementDimensions(containerRef, undefined, undefined, {
    defaultWidth: 600 - legendWidth, // Account for legend width
    defaultHeight: 200,
    widthOffset: legendWidth + 8, // Account for legend width + gap
    heightOffset: 0,
  });

  if (!hourlyData || hourlyData.length === 0 || batteries.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
        No battery data available
      </div>
    );
  }

  const actualWidth = dimensions.width;
  const actualHeight = dimensions.height;
  const margin = 20;
  const leftMargin = 60;
  const timeScale = 24;
  
  // Use the same margin pattern as LoadCurve
  const actualLeftMargin = leftMargin;
  const actualRightMargin = margin;
  const actualTopMargin = margin;
  const actualBottomMargin = 40; // More space for X-axis labels
  
  // Create coordinate conversion functions matching other curves exactly
  const timeToX = (time: number) => (time / timeScale) * (actualWidth - actualLeftMargin - actualRightMargin) + actualLeftMargin;
  const socToY = (soc: number) => actualHeight - actualBottomMargin - (soc / yScale) * (actualHeight - actualTopMargin - actualBottomMargin);

  // Find max SOC for scaling across all hours
  let maxSOC = 0;
  hourlyData.forEach(hourData => {
    if (hourData.batteryStates) {
      batteries.forEach(battery => {
        const state = hourData.batteryStates?.get(battery.id);
        if (state && state.soc > maxSOC) {
          maxSOC = state.soc;
        }
      });
    }
  });

  // Use battery capacity as fallback for max SOC scale
  const maxCapacity = Math.max(...batteries.map(b => b.maxEnergyMWh || 500));
  const yScale = Math.max(maxSOC, maxCapacity);

  // Generate colors for different batteries
  const colors = [
    "#2196F3", "#4CAF50", "#FF9800", "#E91E63", "#9C27B0", "#00BCD4"
  ];

  return (
    <div
      style={{
        padding: "8px",
        background: "#f9f9f9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h4 style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#333" }}>
        Battery State of Charge Over Time
      </h4>
      
      <div
        ref={containerRef}
        style={{ 
          flex: 1, 
          position: "relative", 
          minHeight: 0,
          display: "flex",
          gap: "8px"
        }}
      >
        <div style={{ flex: 1, position: "relative" }}>
          <svg 
            width={actualWidth} 
            height={actualHeight}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              border: "1px solid #ddd",
              background: "white",
              cursor: "default",
              width: "100%",
              height: "100%",
            }}
            onMouseMove={(e) => {
              if (!onHourHover) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const plotX = x - actualLeftMargin;
              if (plotX >= 0 && plotX <= (actualWidth - actualLeftMargin - actualRightMargin)) {
                const hour = Math.round((plotX / (actualWidth - actualLeftMargin - actualRightMargin)) * 23);
                onHourHover(Math.max(0, Math.min(23, hour)));
              }
            }}
            onMouseLeave={() => {
              onHourHover?.(null);
            }}
          >
        {/* Grid */}
        {renderGrid({
          timeToX,
          valueToY: socToY,
          actualWidth,
          actualHeight,
          margin: actualTopMargin,
          timeScale,
          valueScale: yScale,
          leftMargin: actualLeftMargin,
        })}

        {/* Axes */}
        {renderAxes({
          timeToX,
          valueToY: socToY,
          actualWidth,
          actualHeight,
          margin: actualTopMargin,
          timeScale,
          valueScale: yScale,
          valueLabel: "SoC (MWh)",
          valueUnits: "",
          leftMargin: actualLeftMargin,
        })}

        {/* Battery SOC lines */}
        {batteries.map((battery, index) => {
          const color = colors[index % colors.length];
          
          // Create path points for this battery over time
          const pathPoints: string[] = [];
          let lastValidSOC: number | null = null;
          
          hourlyData.forEach((hourData, hourIndex) => {
            const state = hourData.batteryStates?.get(battery.id);
            const soc = state?.soc || lastValidSOC || 0;
            if (state?.soc !== undefined) lastValidSOC = state.soc;
            
            const x = timeToX(hourIndex);
            const y = socToY(soc);
            
            if (hourIndex === 0) {
              pathPoints.push(`M ${x} ${y}`);
            } else {
              pathPoints.push(`L ${x} ${y}`);
            }
          });

          const pathData = pathPoints.join(' ');
          
          // Get final SOC for label
          const finalHour = hourlyData[hourlyData.length - 1];
          const finalState = finalHour?.batteryStates?.get(battery.id);
          const finalSOC = finalState?.soc || 0;

          return (
            <g key={battery.id}>
              <path
                d={pathData}
                fill="none"
                stroke={color}
                strokeWidth="2"
              />
              <text
                x={timeToX(23) - 5}
                y={socToY(finalSOC) - 5}
                textAnchor="end"
                fontSize="10"
                fill={color}
                fontWeight="bold"
              >
                {battery.id}: {finalSOC.toFixed(1)} MWh
              </text>
            </g>
          );
        })}

        {/* Hover line */}
        {hoverHour !== null && hoverHour !== undefined && (
          <line
            x1={timeToX(hoverHour)}
            y1={actualTopMargin}
            x2={timeToX(hoverHour)}
            y2={actualHeight - actualBottomMargin}
            stroke="red"
            strokeWidth="2"
            strokeDasharray="4,4"
          />
        )}

      </svg>
        </div>

        {/* Legend sidebar - matching LoadCurve layout */}
        <div style={{ 
          width: `${legendWidth}px`, 
          minWidth: `${legendWidth}px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          paddingTop: "20px"
        }}>
          <div style={{ fontSize: "10px", color: "#666" }}>
            <p style={{ margin: "0 0 8px 0", fontWeight: "bold", fontSize: "10px" }}>
              Batteries:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {batteries.map((battery, index) => {
                const color = colors[index % colors.length];
                const finalHour = hourlyData[hourlyData.length - 1];
                const finalState = finalHour?.batteryStates?.get(battery.id);
                const finalSOC = finalState?.soc || 0;
                return (
                  <div
                    key={battery.id}
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        backgroundColor: color,
                        borderRadius: "1px",
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: "9px", lineHeight: "1.2" }}>
                      {battery.id} ({finalSOC.toFixed(1)} MWh)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatterySOCChart;